import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllBrands, createBrand, createEngagement, inviteUser, getAllUsers } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Header } from '../../components/layout/Header';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import useDebouncedValue from '../../customHooks/useDebouncedValue';
import { BRAND_CATEGORY, formatDate, formatINR, initials, label, options, today } from '../../constants/garmentCodes';
import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';
import { isValidEmail, validatePassword } from '../../utils/authSession';

const EMPTY_FORM = {
  name: '', legalName: '', primaryContact: '', email: '', phone: '', category: '', gstNumber: '',
  password: '', confirmPassword: '',
  address1: '', city: '', state: '', country: 'India', postalCode: '',
  userName: '', userEmail: '', role: 4,
  enrolmentFee: '50000', openingBalance: '200000', lowBalanceThreshold: '50000', debitApprovalThreshold: '100000',
  engagementName: '', startDate: today(), coordinator: '',
};

const STEPS = ['Basic Details', 'Addresses', 'Brand Users', 'Commercial Setup', 'Engagement Init'];
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

function Field({ label: text, error, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-gray-600 mb-1">{text}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function AddBrandModal({ open, onClose }) {
  const dispatch = useDispatch();
  const manufacturerId = useSelector((state) => state.manufacturer?.profile?.id);
  const users = useSelector((state) => state.user.users);
  // Coordinators are manufacturer-side staff (Manufacturer Admin / Manufacturer Coordinator),
  // not the brand users being invited in step 3.
  const coordinators = users.filter((u) => [1, 2].includes(Number(u.role)));
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) dispatch(getAllUsers({ role: '' }));
  }, [open, dispatch]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Brand name is required';
    if (!form.legalName.trim()) errs.legalName = 'Legal name is required';
    // The brand signs in (pages/LoginPassword) with its email or phone + this password.
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(form.email)) errs.email = 'Enter a valid email address';
    if (!form.phone.trim()) errs.phone = 'Phone is required';
    const passwordError = validatePassword(form.password, form.confirmPassword);
    if (passwordError) {
      // validatePassword only reports a mismatch once the password itself is valid.
      errs[form.password !== form.confirmPassword && !validatePassword(form.password, form.password) ? 'confirmPassword' : 'password'] = passwordError;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setStep(1);
    setErrors({});
  };

  const handleFinish = () => {
    if (!validate()) {
      setStep(1);
      return;
    }
    const f = form;
    const location = [f.address1, f.city, f.state, f.country, f.postalCode].filter(Boolean).join(', ');
    dispatch(
      createBrand({
        payload: {
          name: f.name.trim(),
          legalName: f.legalName.trim(),
          primaryContact: f.primaryContact || null,
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
          password: f.password,
          category: f.category ? Number(f.category) : null,
          gstNumber: f.gstNumber || null,
          location: location || null,
          currency: 'INR',
          primaryColor: '#1b3a6b',
          status: 'Onboarding',
          onboardingStatus: 'Incomplete',
        },
        callback: {
          success: (brand) => {
            if (!brand?.id) return;
            if (f.engagementName.trim() && manufacturerId) {
              dispatch(
                createEngagement({
                  payload: {
                    brandId: brand.id,
                    manufacturerId,
                    name: f.engagementName.trim(),
                    startDate: f.startDate,
                    status: 2,
                    stage: 0,
                    coordinatorUserId: f.coordinator ? Number(f.coordinator) : null,
                    lowBalanceThreshold: f.lowBalanceThreshold ? Number(f.lowBalanceThreshold) : null,
                  },
                }),
              );
            }
            if (f.userName.trim() && f.userEmail.trim()) {
              dispatch(inviteUser({ payload: { name: f.userName.trim(), email: f.userEmail.trim(), role: Number(f.role), brandIds: [brand.id] } }));
            }
          },
        },
      }),
    );
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Brand" size="xl">
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
              i + 1 < step ? 'bg-green-500 text-white' : i + 1 === step ? 'bg-[#1b3a6b] text-white' : 'bg-gray-100 text-gray-400'
            }`}>
              {i + 1 < step ? '✓' : i + 1}
            </div>
            <span className={`text-[11px] font-medium hidden sm:block ${i + 1 === step ? 'text-[#1b3a6b]' : 'text-gray-400'}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-gray-200 hidden sm:block" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Brand Name *" error={errors.name}><input placeholder="Urban Loom" value={form.name} onChange={set('name')} className={inputCls} /></Field>
            <Field label="Legal Name *" error={errors.legalName}><input placeholder="Urban Loom Apparel Pvt Ltd" value={form.legalName} onChange={set('legalName')} className={inputCls} /></Field>
            <Field label="Primary Contact"><input placeholder="Priya Sharma" value={form.primaryContact} onChange={set('primaryContact')} className={inputCls} /></Field>
            <Field label="Email *" error={errors.email}><input placeholder="priya@urbanloom.com" type="email" value={form.email} onChange={set('email')} className={inputCls} /></Field>
            <Field label="Phone *" error={errors.phone}><input placeholder="9876543210" type="tel" value={form.phone} onChange={set('phone')} className={inputCls} /></Field>
            <Field label="Login Password *" error={errors.password}><input type="password" autoComplete="new-password" placeholder="Min 8 characters" value={form.password} onChange={set('password')} className={inputCls} /></Field>
            <Field label="Confirm Password *" error={errors.confirmPassword}><input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={set('confirmPassword')} className={inputCls} /></Field>
            <Field label="Brand Category">
              <select value={form.category} onChange={set('category')} className={inputCls}>
                <option value="">Select category</option>
                {options(BRAND_CATEGORY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="GST Number"><input placeholder="27AABCUL1234L1ZX" value={form.gstNumber} onChange={set('gstNumber')} className={`${inputCls} font-mono`} /></Field>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h4 className="text-[12px] font-semibold text-gray-700">Registered Address</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Field label="Address Line 1"><input placeholder="Street address" value={form.address1} onChange={set('address1')} className={inputCls} /></Field></div>
            <Field label="City"><input placeholder="Mumbai" value={form.city} onChange={set('city')} className={inputCls} /></Field>
            <Field label="State"><input placeholder="Maharashtra" value={form.state} onChange={set('state')} className={inputCls} /></Field>
            <Field label="Country"><input value={form.country} onChange={set('country')} className={inputCls} /></Field>
            <Field label="Postal Code"><input placeholder="400001" value={form.postalCode} onChange={set('postalCode')} className={`${inputCls} font-mono`} /></Field>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name"><input placeholder="Brand representative" value={form.userName} onChange={set('userName')} className={inputCls} /></Field>
            <Field label="Email"><input placeholder="user@brand.com" type="email" value={form.userEmail} onChange={set('userEmail')} className={inputCls} /></Field>
            <Field label="Role">
              <select value={form.role} onChange={set('role')} className={inputCls}>
                <option value={4}>Brand Owner / Administrator</option>
                <option value={5}>Brand Operations User</option>
              </select>
            </Field>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-[12px] text-blue-700">
            An invitation email will be sent to the brand user after the brand is created.
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Enrolment Fee (INR)"><input type="number" value={form.enrolmentFee} onChange={set('enrolmentFee')} className={`${inputCls} font-mono`} /></Field>
            <Field label="Opening Balance Requirement"><input type="number" value={form.openingBalance} onChange={set('openingBalance')} className={`${inputCls} font-mono`} /></Field>
            <Field label="Low-Balance Threshold"><input type="number" value={form.lowBalanceThreshold} onChange={set('lowBalanceThreshold')} className={`${inputCls} font-mono`} /></Field>
            <Field label="Debit Approval Threshold"><input type="number" value={form.debitApprovalThreshold} onChange={set('debitApprovalThreshold')} className={`${inputCls} font-mono`} /></Field>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Engagement Name"><input placeholder={form.name ? `${form.name} — 360 Fashion` : ''} value={form.engagementName} onChange={set('engagementName')} className={inputCls} /></Field>
            <Field label="Start Date"><input type="date" value={form.startDate} onChange={set('startDate')} className={inputCls} /></Field>
            <Field label="Assigned Coordinator">
              <select value={form.coordinator} onChange={set('coordinator')} className={inputCls}>
                <option value="">Select coordinator</option>
                {coordinators.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>
          {!manufacturerId && <p className="text-[11px] text-amber-600">Your manufacturer profile is not set up yet, so the engagement will be skipped.</p>}
          <div className="space-y-2 mt-4">
            <p className="text-[12px] font-semibold text-gray-700">Onboarding Checklist</p>
            {['Brand profile complete', 'Legal details complete', 'Users created', 'Enrolment recorded', 'Wallet initialised', 'Required documents uploaded', 'Engagement activated'].map((item, i) => (
              <div key={item} className="flex items-center gap-2 text-[12px]">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${i < 4 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {i < 4 ? '✓' : ''}
                </div>
                <span className={i < 4 ? 'text-gray-700' : 'text-gray-400'}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
        {step > 1 && <button onClick={() => setStep((s) => s - 1)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">← Back</button>}
        <div className="flex-1" />
        {step < STEPS.length ? (
          <button onClick={() => (step === 1 && !validate() ? null : setStep((s) => s + 1))} className="px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Next →</button>
        ) : (
          <button onClick={handleFinish} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Create Brand</button>
        )}
      </div>
    </Modal>
  );
}

export default function Brands() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  useCreateIntent(CREATE_INTENT.BRAND, () => setAddOpen(true));
  const debouncedSearch = useDebouncedValue(search);
  const brands = useSelector((state) => state.brand.brands);
  const loading = useSelector((state) => state.brand.loading);

  useEffect(() => {
    dispatch(getAllBrands({ name: debouncedSearch.trim(), status: '' }));
  }, [dispatch, debouncedSearch]);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title="Brands" breadcrumbs={[{ workspace: true }, { label: 'Brands' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="flex items-center gap-3 mb-6">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands…"
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-400 bg-white w-52"
          />
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
          <div className="ml-auto">
            <button onClick={() => setAddOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              + Add Brand
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            enableSearch={false}
            exportFileName="Brands"
            columns={[
              { id: 'brand', label: 'BRAND', align: 'left' },
              { id: 'primaryContact', label: 'PRIMARY CONTACT', align: 'left' },
              { id: 'walletBalance', label: 'WALLET BALANCE', align: 'right' },
              { id: 'available', label: 'AVAILABLE', align: 'right' },
              { id: 'held', label: 'HELD', align: 'right' },
              { id: 'engagements', label: 'ENGAGEMENTS', align: 'center' },
              { id: 'pending', label: 'PENDING', align: 'center' },
              { id: 'lastActivity', label: 'LAST ACTIVITY', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={brands.map((brand) => {
              const name = brand.name || '—';
              const status = brand.status || '—';
              const pending = brand.pendingActions ?? 0;
              const engagements = brand.activeEngagements ?? 0;
              return {
                id: brand.id,
                rColumns: [
                  <div key="brand" label={name} className={`flex items-center gap-3 w-full h-full px-2 py-1 ${brand.__optimistic ? 'opacity-60' : ''}`}>
                    {brand.logo
                      ? <img src={brand.logo} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      : <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-[12px] font-bold text-gray-500 flex-shrink-0">{initials(name)}</div>}
                    <div>
                      <button className="text-[13px] font-medium text-[#1b3a6b] hover:underline" onClick={() => navigate(`/dashboard/engagements?brandId=${brand.id}`)}>
                        {name}
                      </button>
                      <p className="text-[10px] text-gray-400">{[brand.legalName, label(BRAND_CATEGORY, brand.category, '')].filter(Boolean).join(' · ')}</p>
                    </div>
                    {brand.lowBalance && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">LOW</span>}
                  </div>,
                  <div key="contact" label={brand.primaryContact || '—'} className="flex flex-col justify-center items-start w-full h-full px-2 py-1">
                    <p className="text-[12px] text-gray-700">{brand.primaryContact || '—'}</p>
                    <p className="text-[11px] text-gray-400">{brand.email || ''}</p>
                  </div>,
                  <div key="wallet" label={formatINR(brand.walletBalance)} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-800">{formatINR(brand.walletBalance)}</div>,
                  <div key="avail" label={formatINR(brand.availableBalance)} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-green-600">{formatINR(brand.availableBalance)}</div>,
                  <div key="held" label={formatINR(brand.heldAmount)} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-amber-600">{formatINR(brand.heldAmount)}</div>,
                  <div key="engs" label={String(engagements)} className="flex items-center justify-center w-full h-full px-2 py-1 text-[12px] text-gray-700">{engagements}</div>,
                  <div key="pend" label={String(pending)} className="flex items-center justify-center w-full h-full px-2 py-1">
                    {pending > 0 ? <span className="text-[11px] font-medium text-amber-600">{pending}</span> : <span className="text-[11px] text-gray-300">—</span>}
                  </div>,
                  <div key="last" label={brand.lastActivity || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-500">{formatDate(brand.lastActivity)}</div>,
                  <div key="status" label={status} className="flex items-center w-full h-full px-2 py-1"><Badge status={status} /></div>,
                  <div key="actions" label="" className="flex items-center gap-2 w-full h-full px-2 py-1">
                    <button onClick={() => navigate(`/dashboard/engagements?brandId=${brand.id}`)} className="text-[11px] text-blue-600 hover:underline">Engagements</button>
                    <button onClick={() => navigate(`/dashboard/wallets?brandId=${brand.id}`)} className="text-[11px] text-gray-400 hover:text-gray-600">Wallet</button>
                  </div>,
                ],
              };
            })}
          />
          {!loading && brands.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No brands found</p>}
        </div>
      </main>
      <AddBrandModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
